/**
 * Chat leads — everyone who left their details on a website widget.
 *
 * Two capture paths feed one list. The pre-chat identity card writes a
 * `ChatVisitor`; the offline contact form, shown when nobody is available, writes
 * the details onto the escalation instead. The server merges them on email, so a
 * visitor who did both is one person here rather than two — the merge happens
 * there rather than in this store because only the server can see both tables.
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  ChatLeadDetailDto,
  ChatLeadDto,
  ChatLeadListDto,
  ChatLeadToContactResultDto,
} from '@respectly/shared-types';

const E = API_ENDPOINTS.SUPER_ADMIN.CHAT;

interface ChatLeadsState {
  leads: ChatLeadDto[];
  /** True when the window was hit — surfaced, never swallowed. */
  truncated: boolean;

  brandId: string | null;
  query: string;

  detail: ChatLeadDetailDto | null;
  detailId: string | null;

  loading: boolean;
  loadingDetail: boolean;
  exporting: boolean;
  adding: boolean;
  error: string | null;

  load: () => Promise<void>;
  setBrand: (brandId: string | null) => Promise<void>;
  setQuery: (query: string) => void;
  openLead: (id: string | null) => Promise<void>;
  exportCsv: () => Promise<boolean>;
  addToContacts: (
    id: string,
    tenantId: string
  ) => Promise<ChatLeadToContactResultDto | null>;
}

function message(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export const useChatLeadsStore = create<ChatLeadsState>((set, get) => ({
  leads: [],
  truncated: false,
  brandId: null,
  query: '',
  detail: null,
  detailId: null,
  loading: false,
  loadingDetail: false,
  exporting: false,
  adding: false,
  error: null,

  load: async () => {
    set({ loading: true, error: null });
    try {
      const { brandId, query } = get();
      const api = getApiClient();
      const result = await api.get<ChatLeadListDto>(E.LEADS, {
        ...(brandId ? { brandId } : {}),
        ...(query.trim() ? { q: query.trim() } : {}),
      });
      set({
        leads: result?.leads ?? [],
        truncated: result?.truncated ?? false,
        loading: false,
      });
    } catch (err) {
      set({ loading: false, error: message(err, 'Failed to load leads') });
    }
  },

  setBrand: async (brandId) => {
    set({ brandId });
    await get().load();
  },

  setQuery: (query) => set({ query }),

  openLead: async (id) => {
    if (!id) {
      set({ detail: null, detailId: null });
      return;
    }
    set({ detailId: id, loadingDetail: true, error: null });
    try {
      const api = getApiClient();
      const detail = await api.get<ChatLeadDetailDto>(E.LEAD(id));
      if (get().detailId !== id) return;
      set({ detail: detail ?? null, loadingDetail: false });
    } catch (err) {
      if (get().detailId !== id) return;
      set({ loadingDetail: false, error: message(err, 'Failed to load the lead') });
    }
  },

  exportCsv: async () => {
    set({ exporting: true, error: null });
    try {
      const { brandId, query } = get();
      const api = getApiClient();
      // Text rather than blob: the api-client exposes the parsed body only, and a
      // CSV is text. Same shape as the PRE bundle download.
      const csv = await api.post<string>(
        E.LEADS_EXPORT,
        { ...(brandId ? { brandId } : {}), ...(query.trim() ? { q: query.trim() } : {}) },
        { responseType: 'text' }
      );
      const url = URL.createObjectURL(new Blob([csv ?? ''], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'chat-leads.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      set({ exporting: false });
      return true;
    } catch (err) {
      set({ exporting: false, error: message(err, 'Failed to export leads') });
      return false;
    }
  },

  addToContacts: async (id, tenantId) => {
    set({ adding: true, error: null });
    try {
      const api = getApiClient();
      const result = await api.post<ChatLeadToContactResultDto>(E.LEAD_TO_CONTACT(id), {
        tenantId,
      });
      set({ adding: false });
      return result ?? null;
    } catch (err) {
      set({ adding: false, error: message(err, 'Failed to add the lead to contacts') });
      return null;
    }
  },
}));
